import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentsIcon from "@mui/icons-material/Payments";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";

import Layout from "../../layout";
import CustomDataTable from "../../components/table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import { getBillsV2, BillV2 } from "../../utils/billingServiceV2";
import moment from "moment";
import BillGenerateModal from "./BillGenerateModal";
import BillDetailsModal from "./BillDetailsModal";
import BillPaymentModal from "./BillPaymentModal";
import BillAdjustmentModal from "./BillAdjustmentModal";
import { fetchGETRequest } from "../../utils/services";

const BillingV2: React.FC = () => {
  const [bills, setBills] = useState<BillV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [customers, setCustomers] = useState<any[]>([]);
  const [sites, setSites] = useState<{ label: string; value: string }[]>([]);

  // Modals
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [paymentBill, setPaymentBill] = useState<BillV2 | null>(null);
  const [adjustmentBill, setAdjustmentBill] = useState<BillV2 | null>(null);

  // Filters
  const [status, setStatus] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [siteId, setSiteId] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [billNumber, setBillNumber] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res: any = await fetchGETRequest("v2/hierarchy");
        if (res && res.success && res.data && res.data.customers) {
          // Set Customers
          setCustomers(
            res.data.customers.map((c: any) => ({
              label: `${c.name}${c.customerCode ? ` (${c.customerCode})` : ""}`,
              value: c.id,
            })),
          );

          // Set Sites
          const allSites: { label: string; value: string }[] = [];
          const siteIds = new Set<string>();

          res.data.customers.forEach((customer: any) => {
            // Sites from directSites
            if (customer.directSites) {
              customer.directSites.forEach((site: any) => {
                if (!siteIds.has(site.id)) {
                  siteIds.add(site.id);
                  allSites.push({ label: site.name, value: site.id });
                }
              });
            }
            // Sites from contracts
            if (customer.contracts) {
              customer.contracts.forEach((contract: any) => {
                if (contract.sites) {
                  contract.sites.forEach((site: any) => {
                    if (!siteIds.has(site.id)) {
                      siteIds.add(site.id);
                      allSites.push({ label: site.name, value: site.id });
                    }
                  });
                }
              });
            }
          });
          setSites(allSites);
        }
      } catch (err) {
        console.error("Error fetching hierarchy data", err);
      }
    };

    fetchData();
  }, []);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Fetching bills with params:", {
        page,
        pageSize: rowsPerPage,
        status,
        customerId,
        siteId,
        fromDate,
        toDate,
        billNumber,
      });
      const response = await getBillsV2({
        page,
        pageSize: rowsPerPage,
        status: status || undefined,
        customerId: customerId || undefined,
        siteId: siteId || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        billNumber: billNumber || undefined,
      });
      console.log("Bills fetch response:", response);

      if (response) {
        // Handle both {success, data} and raw array responses
        if (response.success && response.data) {
          setBills(response.data);
          setTotalCount(response.totalCount || response.data.length);
        } else if (Array.isArray(response)) {
          setBills(response);
          setTotalCount(response.length);
        } else if (response.data && Array.isArray(response.data)) {
          setBills(response.data);
          setTotalCount(response.totalCount || response.data.length);
        }
      }
    } catch (error) {
      console.error("Error fetching bills:", error);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    status,
    customerId,
    siteId,
    fromDate,
    toDate,
    billNumber,
  ]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    "Bill Number",
    "Customer",
    "Contract",
    "Site",
    "Period",
    "Amount",
    "Status",
    "Actions",
  ];

  const rowsContent = bills.map((bill) => (
    <TableRow key={bill.id}>
      <TableCell>{bill.billNumber}</TableCell>
      <TableCell>{bill.customerName}</TableCell>
      <TableCell>{bill.contractNumber}</TableCell>
      <TableCell>{bill.siteName}</TableCell>
      <TableCell>
        {moment(bill.billingPeriodStart).format("DD MMM YY HH:mm")} -{" "}
        {moment(bill.billingPeriodEnd).format("DD MMM YY HH:mm")}
      </TableCell>
      <TableCell>
        ₹{bill.netAmount != null ? (bill.netAmount * -1).toLocaleString() : ""}
      </TableCell>
      <TableCell>
        <Chip
          label={bill.status}
          size="small"
          color={getStatusColor(bill.status) as any}
        />
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          color="primary"
          onClick={() => setSelectedBillId(bill.id)}
          title="View Details"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
        {bill.status === "Approved" && (
          <IconButton
            size="small"
            color="success"
            onClick={() => setPaymentBill(bill)}
            title="Make Payment"
          >
            <PaymentsIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          color="warning"
          onClick={() => setAdjustmentBill(bill)}
          title="Make Bill Adjustment"
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            Bills (v2)
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchBills}
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsGenerateModalOpen(true)}
              sx={{
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              Generate Bill
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
          <Stack direction="row" spacing={2} alignItems="center" width="100%">
            <Box sx={{ flex: 1 }}>
              <CustomOutlinedSelect
                label="Status"
                width="100%"
                options={[
                  { label: "All Status", value: "" },
                  { label: "Draft", value: "Draft" },
                  { label: "Pending", value: "Pending" },
                  { label: "Paid", value: "Paid" },
                  { label: "Approved", value: "Approved" },
                  { label: "Cancelled", value: "Cancelled" },
                ]}
                value={status}
                onChange={(opt) => {
                  setStatus(opt.value as string);
                  setPage(0);
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomInput
                label="Bill Number"
                placeholder="Search Bill Number"
                value={billNumber}
                onChange={(e) => {
                  setBillNumber(e.target.value);
                  setPage(0);
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomOutlinedSelect
                label="Customer"
                width="100%"
                options={[{ label: "All Customers", value: "" }, ...customers]}
                value={customerId}
                onChange={(opt) => {
                  setCustomerId(opt.value as string);
                  setPage(0);
                }}
                placeholder="Select Customer"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomOutlinedSelect
                label="Site"
                width="100%"
                options={[{ label: "All Sites", value: "" }, ...sites]}
                value={siteId}
                onChange={(opt) => {
                  setSiteId(opt.value as string);
                  setPage(0);
                }}
                placeholder="Select Site"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePickerInput
                label="From Date"
                width="100%"
                value={fromDate}
                onChange={(date) => {
                  setFromDate(
                    date
                      ? new Date(
                          date.getTime() - date.getTimezoneOffset() * 60000,
                        ).toISOString()
                      : "",
                  );
                  setPage(0);
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePickerInput
                label="To Date"
                width="100%"
                value={toDate}
                onChange={(date) => {
                  setToDate(
                    date
                      ? new Date(
                          date.getTime() - date.getTimezoneOffset() * 60000,
                        ).toISOString()
                      : "",
                  );
                  setPage(0);
                }}
              />
            </Box>
          </Stack>
        </Card>

        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <CustomDataTable
            columns={columns}
            rowsContent={rowsContent}
            isLoading={loading}
            pagination
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            Actions
          />
        </Card>
      </Box>

      {/* Modals */}
      <BillGenerateModal
        open={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onSuccess={() => {
          setIsGenerateModalOpen(false);
          fetchBills();
        }}
      />

      <BillDetailsModal
        billId={selectedBillId}
        onClose={() => setSelectedBillId(null)}
        onStatusChange={() => {
          setSelectedBillId(null);
          fetchBills();
        }}
      />

      <BillPaymentModal
        open={!!paymentBill}
        bill={paymentBill}
        onClose={() => setPaymentBill(null)}
        onSuccess={() => {
          setPaymentBill(null);
          fetchBills();
        }}
      />

      <BillAdjustmentModal
        open={!!adjustmentBill}
        bill={adjustmentBill}
        onClose={() => setAdjustmentBill(null)}
        onSuccess={() => {
          setAdjustmentBill(null);
          fetchBills();
        }}
      />
    </Layout>
  );
};

export default BillingV2;
